import {
  IBaseWidget,
  isInputWidget,
  IWidgetDirectiveType,
  WidgetType,
} from '@framework/engine';
import { Schema, yup, YupAnyObjectSchema } from '@framework/validation';
import { isDate, set } from 'lodash';
import { IOmissisDirective, OmissisDirectiveSchema } from './IOmissisDirective';
import { OmissisDirectiveConfigComponent } from './OmissisDirectiveConfigComponent';
import { OMISSIS_CONTEXT_KEY, OMISSIS_REPLACE_TEXT } from './omissisConstants';

export function replaceOmissisValue(value: any): any {
  if (value == null) return value;
  if (typeof value === 'string') return OMISSIS_REPLACE_TEXT;
  if (isDate(value)) return OMISSIS_REPLACE_TEXT;
  if (typeof value === 'number') return OMISSIS_REPLACE_TEXT;
  if (typeof value === 'boolean') return null;
  if (Array.isArray(value)) return [];
  // Ogni campo interno viene comunque modificato
  // attraverso l'applicazione del transform a tutti i campi interni
  if (typeof value === 'object') {
    return Object.keys(value).reduce((acc, key) => {
      return set(acc, key, replaceOmissisValue(value[key]));
    }, {});
  }
}

function isObjectSchema(schema: yup.AnySchema): schema is YupAnyObjectSchema {
  return schema.type === 'object';
}

export const OmissisDirectiveType: IWidgetDirectiveType<IOmissisDirective> = {
  name: 'omissis',
  key: 'omissis',
  allowsWidget: (widget, widgetType) =>
    isInputWidget(widget) && widget.type !== WidgetType.Repeatable,
  configComponent: OmissisDirectiveConfigComponent,
  configSchema: OmissisDirectiveSchema,
  formSchemaModifier: ({ directiveConfig, schema, widget, widgetType }) => {
    return schema.when(
      [`$${OMISSIS_CONTEXT_KEY}`],
      ([omissisContextValue]: boolean[], schema) => {
        if (!omissisContextValue) return schema;
        if (!directiveConfig.enabled) return schema;

        // Se ci troviamo in un object schema, dobbiamo applicare la trasformazione anche a
        // tutti i sottocampi.
        // e.g. `<code>@object` di AsyncValue e AsyncSelect
        if (isObjectSchema(schema)) {
          Object.keys(schema.fields).forEach(key => {
            schema.fields[key] =
              // Facciamo un cast a schema per evitare errori di typing
              (schema.fields[key] as Schema).transform(v =>
                replaceOmissisValue(v),
              );
          });
        }

        return schema.transform(v => replaceOmissisValue(v)).notRequired();
      },
    );
  },
};

/**
 * Estrae la configurazione dell'omissis all'interno della direttiva
 * di un widget.
 */
export function isOmissisEnabled(widget: IBaseWidget): boolean {
  return widget.directives?.omissis?.enabled ?? false;
}
